import {Dispatchable} from "../../../stem-core/base/Dispatcher";
import {GlobalState, type RawStateData} from "../../../stem-core/state/State";
import {AuthToken, type SessionToken} from "../AuthToken";
import {apiClient} from "../BlinkApiClient.js";
import {type ConfirmationCodeType, DEFAULT_CONFIRMATION_CODE_TYPE} from "../../../blinkpay/Constants";
import {apiLogoutUser} from "../../state/UserProfileStore";
import {authFormService} from "../../../blinkpay/services/AuthFormService";
import {socialAuthService} from "../../../blinkpay/services/SocialAuthService";
import {wrapInSpinner} from "../../../core/ui/LoadingSpinner.jsx";

// Options Blink adds on top of stem's, read by the interceptors in BaseBlinkApp and here
declare module "../../../stem-core/base/Fetch" {
    interface FetchOptions {
        noAuthToken?: boolean;
        noMerchantOverride?: boolean;
        analytics?: {requestStartTime: number};
    }
}

// The authentication half of a login response: the API serializes the new UserSession under `token`,
// and the merchant-scoped one under `merchantToken` (utils/app_data.py get_login_response_state).
export interface AuthTokenResponse {
    token?: SessionToken | null;
    merchantToken?: SessionToken | null;
    // Added by login_with_email_code when the code was issued with somewhere to return to (users/views.py:196)
    redirectUrl?: string;
    // The rest of the login response: the state the view built, plus whatever the endpoint adds
}


export class AuthService extends Dispatchable {
    static EventTypes = {
        CLEAR_CURRENT_INFO: "clearCurrentInfo", // Token has been cleared, user is now unauthenticated. The stores are going to get cleared.
        AUTHENTICATION_CHANGE: "authenticationChange",
        PUBLIC_DATA_FETCH: "publicDataFetch",
        APP_SETTINGS_UPDATE: "appSettingsUpdate",
        SET_MERCHANT_TOKEN: "setMerchantToken",
        USER_DATA_AVAILABLE: "userDataAvailable",
    };

    token = new AuthToken();
    merchantToken = new AuthToken();
    publicDataState: RawStateData | null = null;
    appSettings: Record<string, any> = {};
    loginAppName: string | null = null;

    constructor() {
        super();

        apiClient.getAjaxHandler().addPreprocessor(options => {
            // We'll allow an overwrite, if we don't want to use the auth token
            if (!options.noAuthToken && this.isAuthenticated()) {
                options.data.auth = this.token.toString();
            }
        });

        // TODO: merge this in all clients
        apiClient.getAjaxHandler().addPostprocessor(response => {
            const {appSettings} = response;
            if (appSettings) {
                this.setAppSettings(appSettings);
            }
        });

        this.token.addListener("change", (tokenEvent: number) => {
            this.dispatch(tokenEvent);
            this.dispatch(AuthService.EventTypes.AUTHENTICATION_CHANGE, tokenEvent);
        });
        this.addListener(AuthToken.EventTypes.LOGOUT, () => this.clearCurrentInfo());

        // If there is a token, will trigger an authentication event, which will fetch execute user fetch
        this.token.load();
    }

    // Used for endpoints that request / confirm an email login code, which for some reason require this information.
    setLoginAppName(loginAppName: string) {
        this.loginAppName = loginAppName;
    }

    setPublicData(data: {state: RawStateData; appSettings?: Record<string, any>}) {
        if (this.publicDataState) {
            return
        }

        this.publicDataState = data.state;
        this.setAppSettings(data.appSettings);

        GlobalState.importState(data.state);
        this.dispatch(AuthService.EventTypes.PUBLIC_DATA_FETCH);
    }

    setAppSettings(settings: Record<string, any> = {}) {
        this.appSettings = settings || {};
        this.dispatch(AuthService.EventTypes.APP_SETTINGS_UPDATE);
    }

    isAuthenticated() {
        return this.token.isAuthenticated();
    }

    @wrapInSpinner
    async loginAtEndpoint(url: string, loginPostData: Record<string, any>) {
        const response = await apiClient.post<AuthTokenResponse>(url, {
            ...loginPostData,
            merchantId: socialAuthService.merchantId,
            appName: this.loginAppName,
        });

        this.setAuthToken(response);

        return response;
    }

    hasAuthToken(response: AuthTokenResponse) {
        return response.token != null || response.merchantToken != null;
    }

    setAuthToken(response: AuthTokenResponse) {
        // Warning: Don't change the order.
        if (response.merchantToken) {
            this.merchantToken.setToken(response.merchantToken.key, response.merchantToken.expiresAt);
            // TODO @cleanup we probably want to dispatch at the end.
            this.dispatch(AuthService.EventTypes.SET_MERCHANT_TOKEN, response.merchantToken);
        } else {
            this.merchantToken.setToken(null, null);
        }

        if (response.token) {
            this.token.setToken(response.token.key, response.token.expiresAt);
        }
    }

    async requestOAuthToken(url: string) {
        return apiClient.post(url, {appName: this.loginAppName});
    }

    async logout() {
        try {
            await apiLogoutUser({useAuthToken: false});
            this.clearCurrentInfo();
        } catch (error) {
            console.debug("User logout failed.");
        }
    }

    clearCurrentInfo() {
        this.token.clear();
        this.dispatch(AuthService.EventTypes.CLEAR_CURRENT_INFO);
        // Clear only the non public stores.
        const publicStateKeys = Object.keys(this.publicDataState || {}).map(key => key.toLowerCase());
        for (const [key, store] of GlobalState.stores) {
            if (!publicStateKeys.find(publicStateKey => publicStateKey === key.toLowerCase())) {
                store.clear && store.clear();
            }
        }
    }

    @wrapInSpinner
    async requestLoginCode(payload: Record<string, any>) {
        // Explicitly don't try to use an auth token here
        const extraOptions = {
            noAuthToken: true
        };
        // Django users/views.py answers with either the login state of a user it registered on the spot,
        // which carries the tokens and the flag, or the type of code it just emailed an existing one
        const response = await apiClient.post<AuthTokenResponse & {registration?: boolean; type?: ConfirmationCodeType}>(
            "/users/request_email_login_code", {
                ...payload,
                merchantId: socialAuthService.merchantId,
                appName: this.loginAppName,
            },
            extraOptions,
        );
        // TODO @cleanup this should be in a post-processor
        if (this.hasAuthToken(response)) {
            // We've been auto-authenticated via registration, since this merchant doesn't care about confirmation codes on new visits
            this.setAuthToken(response);
            this.dispatch(this.constructor.EventTypes.USER_DATA_AVAILABLE, response);
        } else {
            // TODO @flow authFormService should be move inside the auth flow
            authFormService.setIsRegistration(response.registration);
            authFormService.setRegistrationMail(payload.email);
            // The view really does name the field type; renaming it would have to happen on both sides
            authFormService.setConfirmationCodeType(response.type || DEFAULT_CONFIRMATION_CODE_TYPE);
        }

        return response;
    }

    // One of the two: the code the user typed, or the uuid a login link carries in its URL
    async loginWithEmailCode({code, uuid}: {code?: string; uuid?: string}) {
        const response = await apiClient.post<AuthTokenResponse>("/users/login_with_email_code/", {
            email: authFormService.registrationMail,
            code,
            uuid,
            termsAgreed: true,
            appName: this.loginAppName,
        });
        this.setAuthToken(response);

        return response;
    }

    // TODO @flow make this async!
    onAuthentication(callback: () => void) {
        this.addListener(AuthToken.EventTypes.LOGIN, callback);
        if (this.isAuthenticated()) {
            callback();
        }
    }

    // TODO @flow WTF why is this also implemented???? Should be an await as well
    executeWhenAuthenticated(callback: (...args: any[]) => void) {
        if (this.isAuthenticated()) {
            callback();
        } else {
            this.addListenerOnce(AuthToken.EventTypes.LOGIN, callback);
        }
    }

    async waitOnAuth() {
        return new Promise((resolve) => {
            this.executeWhenAuthenticated(resolve);
        })
    }

    onAuthenticationChange(callback: (...args: any[]) => void) {
        this.addListener(AuthService.EventTypes.AUTHENTICATION_CHANGE, callback);
    }
}

export const authService = new AuthService();
